import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Archive, Search as SearchIcon } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { Input } from '@/components/ui/input';
import MediaCard from '@/components/MediaCard';
import { Button } from '@/components/ui/button';
import { useMedia } from '@/contexts/MediaContext';
import { useAuth } from '@/contexts/AuthContext';

const MediaCategoryPage = ({ category, title }) => {
  const { category: categoryParam } = useParams();
  const resolvedCategory = category || categoryParam || '';
  const resolvedTitle = title || resolvedCategory || 'Mídia';

  const { user } = useAuth();
  const { files, loading, loadFiles } = useMedia();
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.uid) {
      loadFiles(user.uid);
    }
  }, [user?.uid, loadFiles]);

  const filteredMedia = useMemo(() => {
    let items = files || [];

    if (resolvedCategory) {
      items = items.filter((item) => {
          const type = (item.type || '').toLowerCase();
          const cat = resolvedCategory.toLowerCase();
          return type.includes(cat) && !item.isDeleted;
      });
    }

    if (searchTerm.trim()) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      items = items.filter((item) =>
        item.name.toLowerCase().includes(lowerSearchTerm) ||
        (item.category || '').toLowerCase().includes(lowerSearchTerm)
      );
    }
    return items;
  }, [resolvedCategory, searchTerm, files]);

  return (
    <>
      <Helmet>
        <title>{resolvedTitle} | Mídia Azul</title>
        <meta name="description" content={`Explore a coleção de ${resolvedTitle.toLowerCase()} na Mídia Azul.`} />
      </Helmet>
      <div className="flex flex-col min-h-full gap-6">
        <motion.header
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="flex flex-col md:flex-row items-center justify-between gap-4 p-6 rounded-3xl bg-slate-900/40 backdrop-blur-md border border-slate-800/50 shadow-lg shadow-black/20"
        >
          <div className="flex-1 w-full md:w-auto">
            <h1 className="text-2xl font-bold text-white mb-1">{resolvedTitle}</h1>
            <p className="text-slate-400 text-sm">Gerencie seus arquivos de {resolvedTitle.toLowerCase()}</p>
          </div>
          
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:w-64">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Pesquisar..."
                className="pl-10 bg-slate-800/50 border-slate-700/50 text-slate-200 placeholder:text-slate-500 focus:ring-cyan-500/50 focus:border-cyan-500/50 rounded-xl"
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button 
              onClick={() => navigate('/')} 
              variant="outline"
              className="border-slate-700 bg-slate-800/50 text-slate-300 hover:bg-slate-700 hover:text-white rounded-xl"
            >
              Voltar
            </Button>
          </div>
        </motion.header>

        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          layout
        >
          <AnimatePresence mode="popLayout">
            {loading ? (
              <div className="col-span-full flex flex-col items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mb-4"></div>
                <p className="text-slate-400">Carregando seus arquivos...</p>
              </div>
            ) : filteredMedia.length > 0 ? (
              filteredMedia.map((item) => (
                <MediaCard
                  key={item.id}
                  item={item}
                />
              ))
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="col-span-full flex flex-col items-center justify-center py-20 text-slate-500"
              >
                <div className="w-24 h-24 bg-slate-800/50 rounded-full flex items-center justify-center mb-6">
                  <Archive className="w-10 h-10 opacity-50" />
                </div>
                <h3 className="text-xl font-semibold text-slate-300 mb-2">Nenhum arquivo encontrado</h3>
                <p className="text-slate-500 max-w-md text-center">
                  Não encontramos arquivos nesta categoria. Que tal fazer um upload?
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </>
  );
};

export default MediaCategoryPage;
